// 260923 E1-P3 — trạng thái 5 bước của plan (dời nguyên văn từ PipelineStepper.tsx để test được dưới jest: file đó import
// antd). Thuần: không gọi API, không đụng state React.
import type { EventPlan } from '../../types/film';
import { needsShots } from './beatSequenceUtils';
import { resolvePromptMode, promptModeStateOf, authorWorkStateOf, canAuthorOf, shouldRebuildMismatchOf } from './promptModeUtils';

export interface StepperStatus {
  nScenes: number; nEvents: number; nShots: number; totalDurationS: number;
  hasDialogue: boolean; hasShots: boolean; hasDurations: boolean; hasContinuity: boolean;
  scriptDone: boolean;
  dcMissing: string[]; outfitMissing: string[]; plateMissing: string[];
  /** 260927 mục I — tên nhân vật (PERSON/ROLE) chưa có ảnh người mặc cho bộ đang dùng trong plan. */
  wornMissing: string[];
  /** 260927 mục I — engine của plan (đọc từ `plan.videoProvider`, thiếu ⇒ AVIS mặc định). PipelineStepper dùng để chọn chữ/điều kiện chặn đúng nhánh. */
  engine: 'AVIS' | 'BYTEPLUS';
  refsReady: boolean; refsBlocked: boolean;
  /**
   * 260923 Epic2-P1 (L12) — `authorDone` = "pass author không còn việc gì làm được": mọi event là `OK`, hoặc
   * bị chặn theo mode mà author không gỡ được (`MISMATCH`; `MISSING` ở mode Tự nhiên — Phase 1 chưa có
   * template). Điều khiển HÀNH VI: mở stage ④ + nút Gen all, quyết `runRemaining` có gọi author không.
   * KHÁC `promptsReady` (chỉ để HIỂN THỊ) — plan Tự nhiên 0 prompt vẫn `authorDone=true` (không có gì author
   * làm được ở Phase 1) nhưng `promptsReady=false` (chưa gen được tới).
   */
  authorDone: boolean;
  /** Chỉ để HIỂN THỊ: mọi event `OK` (snapshot khớp mode hiệu lực) — quyết Stage ③ có xanh hay không. */
  promptsReady: boolean;
  /** = số event `OK` (giữ tên cũ cho chỗ hiển thị "x/y prompts" đã có). */
  nAuthored: number;
  /** Event có chữ nhưng snapshot lệch mode hiệu lực — chặn gen, author không tự gỡ được. */
  nModeMismatch: number;
  /** Event chưa có chữ (mọi mode). */
  nMissing: number;
  /** Event `OK` (gen được) mà CHƯA CHỌN clip — cái Gen all thực sự còn phải làm. */
  nGenReady: number;
  /**
   * 260923 Epic2-P1 F1 — id của mọi event trong `nGenReady` (CÙNG MỘT vị từ, không phải bộ lọc riêng).
   * `PipelineStepper` dùng trực tiếp field này làm `pendingIds` thay vì tự lọc lại (từng lệch: bộ lọc cũ
   * còn loại thêm event `OK` đã có clip DONE nhưng chưa chọn ⇒ đếm ⚙️ override sai — review code E2-P1 F1).
   */
  genReadyIds: string[];
  /** eventOrder của mọi event không `OK` (MISMATCH ∪ MISSING) — nguồn cho Popconfirm/message liệt kê. */
  blockedOrders: number[];
  /**
   * 260923 Epic2-P2 (Bước 7) — tách `blockedOrders` thành 2 nhóm CÁCH GỠ KHÁC NHAU cho câu cảnh báo
   * *Run remaining* khi hết việc: `mismatchOrders` (lệch mode — đổi mode hoặc dựng lại) và
   * `missingBlockedOrders` (MISSING mà `canAuthor=false` — event đời cũ không nhịp / trống / chưa shot).
   */
  mismatchOrders: number[];
  missingBlockedOrders: number[];
  /**
   * 260923 Epic2-P3 (Bước 1) — số event thoả `shouldRebuildMismatchOf` (M1): MISMATCH + canAuthor + KHÔNG
   * EDITED. Dùng cho nút bước Prompts "Dựng lại prompt lệch mode (N)" — N=0 ⇒ ẩn nút.
   */
  nRebuildable: number;
  /**
   * 260927 mục N5 — số event "lệch bộ đồ" (`plan.lookMismatchEventIds`, BE tính LÚC ĐỌC). Dùng cho nút bước
   * Prompts "Dựng lại N prompt lệch bộ đồ" — N=0 ⇒ ẩn nút. Đọc thẳng từ `plan` (không tự suy lại ở FE —
   * khác `nRebuildable`/mode vì so sánh cần bảng gán cảnh mà FE không có sẵn để mirror thuần).
   */
  nLookMismatch: number;
  lookMismatchEventIds: string[];
  clipsDone: number; genDone: boolean;
}

export function computeStatus(data: EventPlan): StepperStatus {
  const events = data.scenes.flatMap((s) => s.events);
  const nEvents = events.length;
  const nShots = events.reduce((a, e) => a + (e.shots?.length || 0), 0);
  const totalDurationS = events.reduce((a, e) => a + (e.genDurationS || 0), 0);
  const hasDialogue = events.some((e) => (e.dialogueLines?.length || 0) > 0);
  // 260923 E1-P3 (S4, R2): "đủ shot" = mọi event CẦN shot đều có shot — CÙNG định nghĩa với BE `runScriptPasses`
  // (mirror `needsShots`; v1 luôn cần ⇒ như cũ). Lệch là UI báo thiếu mà chuỗi không chạy (hoặc ngược lại).
  const hasShots = nEvents > 0 && events.every((e) => !needsShots(e) || (e.shots?.length || 0) > 0);
  const hasDurations = nEvents > 0 && events.every((e) => e.genDurationS != null);
  const hasContinuity = events.some((e) => !!e.continuityLedger);
  const scriptDone = nEvents > 0 && hasShots && hasDurations;

  const chars = data.refBank?.characters || [];
  const locs = data.refBank?.locations || [];
  // 260927 mục I — engine AVIS (mặc định) không còn xét DC (Digital Character): chặn/mở Generate xét ẢNH
  // NGƯỜI MẶC (`hasWornLook`, theo `film_scene_looks` — không có thì bộ chính). Nhánh BytePlus (công tắc tay,
  // User: "giữ hành vi cũ") vẫn xét DC + ma-nơ-canh (`hasOutfit`) như trước — không đổi gì cho nhánh đó.
  const engine: 'AVIS' | 'BYTEPLUS' = data.plan?.engine === 'BYTEPLUS' ? 'BYTEPLUS' : 'AVIS';
  const dcMissing = chars.filter((c) => !c.hasDC).map((c) => c.name);
  const outfitMissing = chars.filter((c) => !c.hasOutfit).map((c) => c.name);
  const wornMissing = chars.filter((c) => !c.hasWornLook).map((c) => c.name);
  const refBlockMissing = engine === 'BYTEPLUS' ? dcMissing : wornMissing;
  const plateMissing = locs.filter((l) => !l.hasPlate).map((l) => l.name);
  const refsReady = refBlockMissing.length === 0 && plateMissing.length === 0
    && (engine === 'BYTEPLUS' ? outfitMissing.length === 0 : true);
  const refsBlocked = refBlockMissing.length > 0; // AVIS: thiếu ảnh người mặc · BytePlus: thiếu DC — chặn gen (mặt/đồ sẽ sai)

  /**
   * 260923 Epic2-P1 (Bước 8, L12) — tính LẠI mode/trạng thái từng event bằng mirror thuần (không tin field
   * `effectivePromptMode`/`promptModeState`/`authorWork` BE gửi kèm — tính tại đây để plan CŨ (payload
   * chưa từng qua BE mới, ví dụ fixture test) vẫn ra kết quả đúng thay vì `undefined`).
   */
  const planMode = (data.plan as any)?.promptMode;
  const evStates = events.map((e) => {
    const effectiveMode = resolvePromptMode((e as any).promptMode, planMode);
    const state = promptModeStateOf({ effectiveMode, authorPrompt: e.authorPrompt, authorPromptMode: (e as any).authorPromptMode });
    // 260925 (D1, sửa sau blackbox bước 1–2) — event CHỈ CÓ THOẠI (0 nhịp) vẫn phải `canAuthor` được, nếu không
    // pass author bị chặn vĩnh viễn trên một event Epic 1 hợp lệ. `spokenLineCount` = số câu `mode ≠ NONE` có chữ.
    const spokenLineCount = ((e.dialogueLines || []) as any[]).filter((d) => d?.textEn && String(d.dialogueMode || '').toUpperCase() !== 'NONE').length;
    const canAuth = canAuthorOf({ mode: effectiveMode, beatMeta: (e as any).beatMeta, shotCount: (e.shots || []).length, spokenLineCount });
    // 260923 Epic2-P3 (Bước 1) — CÙNG vị từ M1 dùng cho nút bước Prompts (nRebuildable, dưới) và cho FE
    // tự dựng lại 1 event sau khi đổi mode (M2, EventPlanTab).
    const rebuildable = shouldRebuildMismatchOf({
      effectiveMode, authorPrompt: e.authorPrompt, authorPromptMode: (e as any).authorPromptMode,
      authorStatus: (e as any).authorStatus, beatMeta: (e as any).beatMeta, shotCount: (e.shots || []).length, spokenLineCount,
    });
    return { e, effectiveMode, state, work: authorWorkStateOf(state, effectiveMode, canAuth), rebuildable };
  });
  const nAuthored = evStates.filter((x) => x.state === 'OK').length;
  const nModeMismatch = evStates.filter((x) => x.state === 'MISMATCH').length;
  const nMissing = evStates.filter((x) => x.state === 'MISSING').length;
  const nRebuildable = evStates.filter((x) => x.rebuildable).length;
  const blockedOrders = evStates.filter((x) => x.state !== 'OK').map((x) => x.e.eventOrder);
  const mismatchOrders = evStates.filter((x) => x.state === 'MISMATCH').map((x) => x.e.eventOrder);
  const missingBlockedOrders = evStates.filter((x) => x.state === 'MISSING' && x.work === 'blocked').map((x) => x.e.eventOrder);
  const authorDone = nEvents > 0 && evStates.every((x) => x.work !== 'todo');
  const promptsReady = nEvents > 0 && nAuthored === nEvents;
  const genReadyIds = evStates.filter((x) => x.state === 'OK' && !x.e.selectedClipId).map((x) => x.e.id);
  const nGenReady = genReadyIds.length;

  const clipsDone = events.filter((e) => !!e.selectedClipId || (e.clips || []).some((c) => c.status === 'DONE')).length;
  const genDone = nEvents > 0 && clipsDone === nEvents;

  const lookMismatchEventIds = (data.plan as any)?.lookMismatchEventIds || [];

  return {
    nScenes: data.scenes.length, nEvents, nShots, totalDurationS, hasDialogue, hasShots, hasDurations, hasContinuity, scriptDone,
    dcMissing, outfitMissing, plateMissing, wornMissing, engine, refsReady, refsBlocked,
    authorDone, promptsReady, nAuthored, nModeMismatch, nMissing, nGenReady, genReadyIds, blockedOrders,
    mismatchOrders, missingBlockedOrders, nRebuildable,
    nLookMismatch: lookMismatchEventIds.length, lookMismatchEventIds,
    clipsDone, genDone,
  };
}

/**
 * 260927 mục K — "References còn thiếu" sau khi kịch bản đã xong: cảnh chưa chia bộ đồ (`sceneLooksCount===0`)
 * · bối cảnh thiếu ảnh (`plateMissing`) · (BytePlus) trang phục thiếu ảnh ma-nơ-canh (`outfitMissing`).
 *
 * 🔴 Vì sao gọi `runScriptPasses` thay vì tự làm 3 lượt riêng (`proposeSceneLooks`/`genLocationPlates`/
 * `prepareOutfits`, cách `runRemaining` làm trước đợt này): chuỗi mục E (BE, cùng nền idempotent) làm
 * ĐÚNG BỐN việc theo thứ tự — bộ đồ từ cast (nhân vật vai lặp/ROLE mới promote còn thiếu hồ sơ/bộ đồ,
 * mục H) → chia bộ đồ theo cảnh → ảnh nền bối cảnh → đạo cụ vào Bank (mục F) — nên MỘT lượt gọi này xử lý
 * cả hai việc "nhân vật chưa hồ sơ/bộ đồ" và "đạo cụ chưa vào Bank" mà FE không cần tự biết riêng từng cái
 * (refBank hiện không có field lộ ra hai tín hiệu đó). Không `force` — idempotent, bỏ qua cái đã có ảnh.
 */
export function referencesIncomplete(s: StepperStatus, sceneLooksCount: number): boolean {
  return sceneLooksCount === 0 || s.plateMissing.length > 0
    || (s.engine === 'BYTEPLUS' && s.outfitMissing.length > 0);
}

/**
 * 260925 (UT6, sửa sau nghiệm thu Epic 2) — `anyBusy` của `PipelineStepper` (quyết nút "Run remaining steps"
 * có quay hay không): khoá `rebuild-<eventId>` (nút thẻ "Dựng lại prompt" của MỘT event — `EventPlanTab`)
 * KHÔNG được tính vào — người bấm nút thẻ (1 lượt AI cho 1 event) không nên thấy nút "Run remaining steps"
 * (thao tác tốn nhiều lượt AI hơn hẳn) quay theo, tưởng mình lỡ bấm nhầm nó. Khoá khác (`gen-<eventId>`,
 * `author`, `rebuildMismatch`, …) GIỮ NGUYÊN — có từ trước Phase này.
 */
export function isAnyBusy(busy: Record<string, boolean>): boolean {
  return Object.entries(busy).some(([k, v]) => v && !k.startsWith('rebuild-'));
}
